import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  getDoc,
  documentId,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '../firebase'
// Types
export interface Task {
  id?: string
  title: string
  studentId: string
  studentName?: string
  dueDate: string | null
  startDate?: string | null
  notes: string
  status: 'pending' | 'in-progress' | 'completed'
  reference?: string
  lessonId?: string
  createdAt?: string | null
  updatedAt?: string | null
  completedDate?: string | null // Added: timestamp when task was marked completed
}
// Global cache to store tasks
// This will persist across component unmounts/remounts during navigation
export const tasksCache = {
  data: null,
  timestamp: 0,
  userId: null,
}
// Task cache validity duration - 5 minutes
export const CACHE_VALIDITY_DURATION = 5 * 60 * 1000
// Cache to store student name lookup results
const studentNameCache: Record<string, { name: string; timestamp: number }> = {}
const STUDENT_CACHE_VALIDITY = 30 * 60 * 1000 // 30 minutes
// P0 SCALABILITY FIX: Batch size for student lookups
const BATCH_SIZE = 10 // Firestore 'in' query limit
// SPAM-CLICK PROTECTION: Lock flag to prevent concurrent task creation
let isCreatingTask = false
/**
 * Get all tasks for a teacher - optimized version with batch student fetching
 * @param teacherId The ID of the teacher
 * @returns Array of task objects
 */
export const getTeacherTasks = async (teacherId: string): Promise<Task[]> => {
  if (!teacherId) {
    console.error('No teacher ID provided to getTeacherTasks')
    return []
  }

  try {
    console.log(
      '[P0 FIX] Starting optimized task fetch with batch student lookup for teacher:',
      teacherId,
    )

    // Step 1: Fetch all tasks
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(tasksRef, orderBy('dueDate', 'asc'))
    const tasksSnapshot = await getDocs(tasksQuery)

    console.log(
      `[P0 FIX] Found ${tasksSnapshot.docs.length} tasks in main collection`,
    )

    if (tasksSnapshot.empty) {
      return []
    }

    // Step 2: Extract unique student IDs
    const uniqueStudentIds = new Set<string>()
    const taskDocs = tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      if (data.studentId) {
        uniqueStudentIds.add(data.studentId)
      }
      return { id: doc.id, data }
    })

    console.log(
      `[P0 FIX] Found ${uniqueStudentIds.size} unique students across ${taskDocs.length} tasks`,
    )

    // Step 3: Batch-fetch student names (Firestore 'in' query limit is 10)
    const studentIdArray = Array.from(uniqueStudentIds)
    const studentNameMap = new Map<string, string>()

    // Process in batches of 10 (Firestore limit for 'in' queries)
    for (let i = 0; i < studentIdArray.length; i += BATCH_SIZE) {
      const batch = studentIdArray.slice(i, i + BATCH_SIZE)

      try {
        const studentsRef = collection(db, 'teachers', teacherId, 'students')
        const studentsQuery = query(
          studentsRef,
          where(documentId(), 'in', batch),
        )
        const studentsSnapshot = await getDocs(studentsQuery)

        studentsSnapshot.forEach((studentDoc) => {
          const studentData = studentDoc.data()
          studentNameMap.set(
            studentDoc.id,
            studentData.name || 'Unknown Student',
          )
        })

        console.log(
          `[P0 FIX] Batch ${Math.floor(i / BATCH_SIZE) + 1}: Fetched ${studentsSnapshot.size} student names`,
        )
      } catch (err) {
        console.error(
          `[P0 FIX] Error fetching student batch ${Math.floor(i / BATCH_SIZE) + 1}:`,
          err,
        )
      }
    }

    console.log(`[P0 FIX] Total student names fetched: ${studentNameMap.size}`)

    // Step 4: Map tasks with student names
    const tasks: Task[] = taskDocs.map(({ id, data }) => {
      const studentId = data.studentId || ''
      const studentName =
        studentNameMap.get(studentId) || data.studentName || 'Unknown Student'

      return {
        id,
        title: data.title || '',
        studentId,
        studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
      } as Task
    })

    // Sort by due date
    tasks.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0)
      const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0)
      return dateA.getTime() - dateB.getTime()
    })

    console.log(
      `[P0 FIX] Processed ${tasks.length} tasks with batched student names`,
    )
    console.log(
      `[P0 FIX] Firestore reads: 1 (tasks) + ${Math.ceil(uniqueStudentIds.size / BATCH_SIZE)} (students in batches) = ${1 + Math.ceil(uniqueStudentIds.size / BATCH_SIZE)} total`,
    )

    return tasks
  } catch (error) {
    console.error('[P0 FIX] Error fetching teacher tasks:', error)
    return []
  }
}
/**
 * Get tasks for a specific student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @returns Array of task objects for the student
 */
export const getStudentTasks = async (
  teacherId: string,
  studentId: string,
): Promise<Task[]> => {
  if (!teacherId || !studentId) {
    console.error('Missing teacher ID or student ID')
    return []
  }
  try {
    console.log(`Fetching tasks for student document ID: ${studentId}`)
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(
      tasksRef,
      where('studentId', '==', studentId),
      orderBy('dueDate', 'asc'),
    )
    const tasksSnapshot = await getDocs(tasksQuery)
    let studentName = 'Unknown Student'
    try {
      const studentDoc = await getDoc(
        doc(db, 'teachers', teacherId, 'students', studentId),
      )
      if (studentDoc.exists()) {
        studentName = studentDoc.data().name || 'Unknown Student'
      }
    } catch (err) {
      console.error(`Error fetching student name for ${studentId}:`, err)
    }
    return tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        title: data.title || '',
        studentId: studentId,
        studentName: studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
      } as Task
    })
  } catch (error) {
    console.error('Error fetching student tasks:', error)
    return []
  }
}
/**
 * Add a new task for a student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param task The task data to add
 * @returns The ID of the newly created task
 */
export const addTask = async (
  teacherId: string,
  studentId: string,
  task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> => {
  // SPAM-CLICK PROTECTION: Check if task creation is already in progress
  if (isCreatingTask) {
    throw new Error('Task creation already in progress. Please wait.')
  }

  if (!teacherId || !studentId) {
    throw new Error('Missing teacher ID or student ID')
  }

  // SPAM-CLICK PROTECTION: Set lock before starting
  isCreatingTask = true

  try {
    console.log(`Adding task for student document ID: ${studentId}`)

    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')

    let dueDate = task.dueDate
    if (typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = null
    if (task.startDate && typeof task.startDate === 'string') {
      const dateObj = new Date(task.startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (task.startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = task.startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    const taskData: any = {
      title: task.title,
      studentId: studentId,
      studentName: task.studentName || '',
      dueDate: dueDate,
      startDate: startDate,
      notes: task.notes || '',
      status: task.status || 'pending',
      reference: task.reference || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    // Add lessonId if provided
    if (task.lessonId) {
      taskData.lessonId = task.lessonId
    }

    const docRef = await addDoc(tasksRef, taskData)
    console.log('Task added with ID:', docRef.id)

    if (task.studentName) {
      studentNameCache[studentId] = {
        name: task.studentName,
        timestamp: Date.now(),
      }
    }

    return docRef.id
  } catch (error) {
    console.error('Error adding task:', error)
    throw error
  } finally {
    // SPAM-CLICK PROTECTION: Always release lock, even on error
    isCreatingTask = false
  }
}
/**
 * Update an existing task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to update
 * @param task The updated task data
 * @returns A boolean indicating success
 */
export const updateTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
  task: Partial<Task>,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }

  try {
    console.log(`Updating task ${taskId} for student document ID: ${studentId}`)

    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)

    let dueDate = task.dueDate
    if (dueDate && typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = task.startDate
    if (startDate && typeof startDate === 'string') {
      const dateObj = new Date(startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    const updateData: any = {
      updatedAt: serverTimestamp(),
    }

    if (task.title !== undefined) updateData.title = task.title
    if (dueDate !== undefined && dueDate !== null) updateData.dueDate = dueDate
    if (startDate !== undefined && startDate !== null)
      updateData.startDate = startDate
    if (task.notes !== undefined) updateData.notes = task.notes
    if (task.status !== undefined) {
      updateData.status = task.status
      // Add completedDate when task is marked as completed
      if (task.status === 'completed') {
        updateData.completedDate = serverTimestamp()
      }
    }
    if (task.reference !== undefined) updateData.reference = task.reference

    // Handle lessonId updates
    if (task.lessonId !== undefined) {
      updateData.lessonId = task.lessonId
    }

    await updateDoc(taskRef, updateData)
    console.log('Task updated successfully:', taskId)

    return true
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}
/**
 * Delete a task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to delete
 * @returns A boolean indicating success
 */
export const deleteTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }
  try {
    console.log(`Deleting task ${taskId} for student document ID: ${studentId}`)
    // Delete from main tasks collection
    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)
    await deleteDoc(taskRef)
    console.log('Task deleted successfully:', taskId)
    return true
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}
